import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';

const waiting = new Map();
let loop = false;
let webp;

/**
 * Global animation frame to check for images in view
 */
const scrollFrame = () => {
    waiting.forEach((image, key) => {
        if (image?.imageRef?.current?.offsetParent) {
            const r = image.imageRef.current.getBoundingClientRect();
            if (r.top - r.height - window.innerHeight <= 0) {
                image.load();
                waiting.delete(key);
            }
        }
    });

    window.requestAnimationFrame(scrollFrame);
};

/**
 * Load all images
 */
const loadAll = () => {
    waiting.forEach((image, key) => {
        image.load();
        waiting.delete(key);
    });
};

/**
 * Hook to confirm WEBP browser support
 */
export const useWebp = async () => {
    useEffect(() => {
        if (typeof webp !== 'boolean') {
            (async () => {
                if (!self.createImageBitmap) { return; }
                const webpData = 'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=';
                const blob = await fetch(webpData).then(r => r.blob());
                await createImageBitmap(blob).then(
                    () => webp = true,
                    () => webp = false
                );
            })();

        }
    }, []);

    return webp;
};

/**
 * Hook to manage image file load on scroll
 * @param {object} options
 * @param {object} options.imageRef
 * @param {string} options.src
 * @returns
 */
export const useLazyImage = ({
    imageRef,
    src,
}) => {
    const [uid, setUid] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const load = async () => {
        setLoading(true);
    };

    useEffect(() => {
        if (loading && imageRef.current) {
            let imageNode = imageRef.current;
            const imageSrc = imageNode.currentSrc || src;

            if (imageSrc) {
                imageNode.imageSrc = imageSrc;

                const onLoad = () => {
                    setLoading(false);
                    setLoaded(true);
                };

                imageNode.complete ? onLoad() : imageNode.onload = onLoad;
            }
        }
    }, [loading, imageRef.current]);

    useEffect(() => {
        if (!uid) {
            const key = nanoid();
            setUid(key);

            // Register image
            waiting.set(key, {
                imageRef,
                load
            });

            // Trigger all images to load when printing document
            if (!window.onbeforeprint) {
                window.onbeforeprint = loadAll;
            }

            // Start loop
            if (!loop) {
                loop = true;
                window.requestAnimationFrame(scrollFrame);
            }
        }

        return () => { waiting.delete(uid); };
    }, []);

    return {
        loading,
        loaded
    };
};
